"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SensitivityLevelSchema = exports.BeliefSystemSchema = exports.FactCheckHistoryItemSchema = exports.FactCheckResponseSchema = exports.SourceSchema = exports.VERDICT_LABEL = exports.VerdictSchema = exports.FactCheckFeedbackSchema = exports.FactCheckRequestSchema = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
// Fact Check Request
exports.FactCheckRequestSchema = zod_1.z.object({
    query: zod_1.z.string().min(5).max(5000),
    priority: zod_1.z.enum(['standard', 'fast']).default('standard'),
    context: zod_1.z.string().max(1000).optional(),
    userContext: zod_1.z.object({
        previousBeliefs: zod_1.z.array(zod_1.z.string()).optional(),
        sensitivityPreferences: zod_1.z.string().optional(),
    }).optional(),
});
// Explicit user feedback on a verification (thumbs / report) — the ground-truth
// signal for the verification-improvement loop.
exports.FactCheckFeedbackSchema = zod_1.z.object({
    signal: zod_1.z.enum(['helpful', 'not_helpful', 'report']),
    reason: zod_1.z.string().max(2000).optional(),
    /** Optional chat_messages id of the exact turn reacted to (elaborations have no fact_checks row). */
    messageId: zod_1.z.string().uuid().optional(),
});
// Verdict Types
exports.VerdictSchema = zod_1.z.enum([
    'strongly_supported',
    'largely_supported',
    'balanced',
    'limited_support',
    'insufficient'
]);
/**
 * Canonical noun-phrase display labels for verdicts — THE single source of
 * truth for every surface (web, mobile, extension, OG link unfurls). Rendered
 * as "{label} of the claim · {score}% confidence in our analysis".
 *
 * Do NOT redeclare this map locally in an app: a diverged copy is exactly how
 * a WhatsApp preview ("Strongly supported") ended up disagreeing with the page
 * it linked to ("Strong support"). Native code that cannot import this package
 * (e.g. the iMessage extension's TreuDesign.swift) mirrors it and must be kept
 * in sync by hand.
 */
exports.VERDICT_LABEL = {
    strongly_supported: 'Strong support',
    largely_supported: 'Broad support',
    balanced: 'Mixed evidence',
    limited_support: 'Limited support',
    insufficient: 'Insufficient evidence',
};
// Source Information
exports.SourceSchema = zod_1.z.object({
    url: zod_1.z.string().url(),
    title: zod_1.z.string(),
    credibilityScore: zod_1.z.number().min(0).max(100),
    relevance: zod_1.z.number().min(0).max(100),
    publishedDate: common_1.DateSchema.optional(),
    sourceType: zod_1.z.enum(['wikipedia', 'googleFactCheck', 'pubmed', 'newsApi', 'cdc', 'nih', 'census', 'arxiv', 'bbc', 'web']).optional(),
    factCheckRating: zod_1.z.string().optional(), // Textual rating from fact-checking organizations (e.g., "False", "Mostly True")
    // A short excerpt of the source's ACTUAL content (article lead, abstract, fact-check
    // claim text). Fed into the verification prompt so the model grounds its verdict in
    // what the source says — not just that a page with this title exists. Length-capped to
    // bound prompt token/cost. Populated by the aggregator; stripped before the API boundary
    // (the client UI never reads it).
    snippet: zod_1.z.string().max(1200).optional(),
});
// Fact Check Response
exports.FactCheckResponseSchema = zod_1.z.object({
    id: common_1.UUIDSchema,
    confidence: zod_1.z.number().min(0).max(100),
    verdict: exports.VerdictSchema,
    reasoning: zod_1.z.string(),
    psychologicalFraming: zod_1.z.string(),
    sources: zod_1.z.array(exports.SourceSchema),
    beliefSystemDetected: zod_1.z.string().optional(),
    processingTimeMs: zod_1.z.number(),
    fromCache: zod_1.z.boolean(),
    costInfo: common_1.CostInfoSchema.optional(),
    createdAt: common_1.DateSchema,
    // Optional fields for limit exceeded responses and display
    type: zod_1.z.enum(['trial_limit_exceeded', 'monthly_limit_exceeded']).optional(),
    confidenceLevel: zod_1.z.string().optional(),
    // Digestible / conversational response fields (optional ⇒ old stored result JSONB still validates)
    headline: zod_1.z.string().optional(), // Short (≤~8-word) verdict phrase
    shortAnswer: zod_1.z.string().optional(), // 1–2 sentence digestible summary (the app injects the % confidence)
    fullAnalysis: zod_1.z.string().optional(), // The long "full analysis" (mirrors reasoning, exposed at the response boundary)
    confidenceBasis: zod_1.z.enum(['sources', 'ai_knowledge', 'web_search']).optional(), // Whether the score came from deterministic sources, AI knowledge, or a live grounded web search
    knowledgeBasis: zod_1.z.string().optional(), // One sentence explaining the AI-knowledge basis (only when confidenceBasis === 'ai_knowledge')
    kind: zod_1.z.enum(['verification', 'elaboration']).optional(), // Distinguishes a full verification turn from a conversational follow-up
});
// Fact Check History Item
exports.FactCheckHistoryItemSchema = zod_1.z.object({
    id: common_1.UUIDSchema,
    query: zod_1.z.string(),
    verdict: exports.VerdictSchema,
    confidence: zod_1.z.number(),
    createdAt: common_1.DateSchema,
    fromCache: zod_1.z.boolean(),
});
// Belief System Types (server-side only, never exposed to frontend)
exports.BeliefSystemSchema = zod_1.z.enum([
    'flat_earth',
    'vaccine_hesitancy',
    'climate_skepticism',
    'election_denial',
    'conspiracy_general',
    'health_misinformation',
    'financial_conspiracy',
    'none_detected'
]);
// Sensitivity Levels
exports.SensitivityLevelSchema = zod_1.z.enum(['low', 'medium', 'high', 'very_high']);
//# sourceMappingURL=fact-check.js.map